import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Steps,
    Card,
    Button,
    Space,
    Typography,
    Descriptions,
    Tag,
    Spin,
    Form as AntDForm
} from 'antd';
import {
    PictureOutlined,
    InfoCircleOutlined,
    EnvironmentOutlined,
    ToolOutlined,
    CheckCircleOutlined,
    SaveOutlined,
    CloseOutlined,
    EditOutlined,
    LockOutlined
} from '@ant-design/icons';
import { axiosReq } from "../../api/axiosDefaults";
import { useRedirect } from "../../hooks/useRedirect";
import useUserStatus from "../../hooks/useUserStatus";
import useFetchOwners from "../../hooks/useFetchOwners";
import Forbidden403 from "../errors/Forbidden403";
import DragDropImageUpload from "../../components/DragDropImageUpload";
import TypeSaleSub from "../../components/createEditFormFields/TypeSaleSub";
import CurrencyPriceAvailability from "../../components/createEditFormFields/CurrencyPriceAvailability";
import Description from "../../components/createEditFormFields/Description";
import LocationFields from "../../components/createEditFormFields/LocationFields";
import ResidentialFields from "../../components/createEditFormFields/ResidentialFields";
import CommercialFields from "../../components/createEditFormFields/CommercialFields";
import LandFields from "../../components/createEditFormFields/LandFields";
import { useTranslation } from "react-i18next";
import Owner from "../../components/createEditFormFields/Owner";
import Form from "react-bootstrap/Form";
import styles from "../../styles/ListingCreateEditForm.module.css";
import { Alert } from "react-bootstrap";
import { App } from 'antd';
import useFetchLocationData from "hooks/useFetchLocationData";
import { AmenitiesLand } from "components/createEditFormFields/amenities/AmenitiesLand";
import { AmenitiesResidential } from "components/createEditFormFields/amenities/AmenitiesResidential";
import { AmenitiesCommercial } from "components/createEditFormFields/amenities/AmenitiesCommercial";
import { ApprovedFeatureCheckbox } from "components/createEditFormFields/ApprovedFeatureCheckbox";
import Input from "antd/es/input/Input";

const { Title } = Typography;

function AdminListingCreateForm() {
    // Authentication and authorization hooks
    useRedirect("loggedOut");
    const userStatus = useUserStatus();

    // Translation and routing
    const { t, i18n } = useTranslation();
    const lng = i18n?.language;
    const history = useHistory();

    const { message: antMessage } = App.useApp();
    // Modal state for Owner creation
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    //
    const [existingImages, setExistingImages] = useState([]);
    const [imagesToDelete, setImagesToDelete] = useState([]);

    // Fetch owners data
    const { owners, hasLoaded: ownersLoaded } = useFetchOwners();
    const owner = owners.reduce((map, owner) => {
        map[owner.id] = `${owner.first_name} ${owner.last_name}`;
        return map;
    }, {});
    // Location data
    const { regionsData } = useFetchLocationData();

    // Form state

    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("");
    const [selectedMunicipality, setSelectedMunicipality] = useState("");
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [draftId, setDraftId] = useState(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);

    const [listingData, setListingData] = useState({
        type: "",
        sub_type: "",
        sale_type: "",
        price: "",
        currency: "€",
        description: "",
        description_gr: "",
        address_number: "",
        address_street: "",
        address_street_gr: "",
        postcode: "",
        municipality_gr: "",
        floor_area: "",
        land_area: "",
        levels: "",
        bedrooms: "",
        wc: "",
        floor: "",
        kitchens: "",
        bathrooms: "",
        living_rooms: "",
        heating_system: "",
        energy_class: "",
        power_type: "",
        floor_type: "",
        construction_year: "",
        latitude: "0.0",
        longitude: "0.0",
        cover_coefficient: "",
        building_coefficient: "",
        length_of_facade: "",
        orientation: "",
        view: "",
        slope: "",
        zone: "",
        distance_from_sea: "",
        rooms: "",
        approved: false,
        featured: false,
        region_id: "",
        county_id: "",
        municipality_id: "",
        listing_owner: "",
        video_url: "",
    });

    const [errors, setErrors] = useState({});

    const handleChecked = (e) => {
        setListingData({
            ...listingData,
            [e.target.name]: e.target.checked,
        });
    };

    const handleChange = (e) => {
        setListingData({
            ...listingData,
            [e.target.name]: e.target.value,
        });
    };

    const handleAmenityChange = (amenityId) => {
        setSelectedAmenities((prev) =>
            prev.includes(amenityId)
                ? prev.filter((id) => id !== amenityId)
                : [...prev, amenityId]
        );
    };

    const handleRegionChange = (region) => {
        setSelectedRegion(region);
        setListingData((prevData) => ({
            ...prevData,
            region_id: region,
        }));
        setSelectedCounty("");
        setSelectedMunicipality("");
    };

    const handleCountyChange = (county) => {
        setSelectedCounty(county);
        setListingData((prevData) => ({
            ...prevData,
            county_id: county,
        }));
        setSelectedMunicipality("");
    };

    const handleMunicipalityChange = (municipality, municipality_gr, municipality_en) => {
        setSelectedMunicipality(municipality);
        setListingData((prevData) => ({
            ...prevData,
            municipality_id: municipality,
            municipality_gr: municipality_gr,
            municipality: municipality_en || prevData.municipality,
        }));
    };

    const regionNameEn = regionsData
        .find((region) => region.id === selectedRegion)
        ?.englishName || "";
    const regionNameGr = regionsData
        .find((region) => region.id === selectedRegion)
        ?.greekName || "";

    const countyNameEn = regionsData
        .find((region) => region.id === selectedRegion)
        ?.counties.find((county) => county.id === selectedCounty)
        ?.englishName || "";

    const countyNameGr = regionsData
        .find((region) => region.id === selectedRegion)
        ?.counties.find((county) => county.id === selectedCounty)
        ?.greekName || "";

    const municipalityNameGr = regionsData
        .find((region) => region.id === selectedRegion)
        ?.counties.find((county) => county.id === selectedCounty)
        ?.municipalities.find((municipality) => municipality.id === selectedMunicipality)
        ?.greekName || "";

    const municipalityNameEn = regionsData
        .find((region) => region.id === selectedRegion)
        ?.counties.find((county) => county.id === selectedCounty)
        ?.municipalities.find((municipality) => municipality.id === selectedMunicipality)
        ?.englishName || "";

    const regionName = lng === 'el' ? regionNameGr : regionNameEn;
    const countyName = lng === 'el' ? countyNameGr : countyNameEn;
    const municipalityName = lng === 'el' ? municipalityNameGr : municipalityNameEn;


    const validateStep = (step) => {
        const newErrors = {};

        switch (step) {
            case 0:
                break;
            case 1:
                if (!listingData.type) newErrors.type = [t("admin.listingsForms.stepErrors.typeIsRequired")];
                if (!listingData.sale_type) newErrors.sale_type = [t("admin.listingsForms.stepErrors.saleTypeIsRequired")];
                if (!listingData.price) newErrors.price = [t("admin.listingsForms.stepErrors.priceIsRequired")];
                if (!listingData.listing_owner) newErrors.listing_owner = [t("admin.listingsForms.stepErrors.ownerIsRequired")];
                if (!listingData.description || !listingData.description_gr) newErrors.description = [t("admin.listingsForms.stepErrors.descriptionIsRequired")];
                if (!listingData.description_gr || !listingData.description) newErrors.description_gr = [t("admin.listingsForms.stepErrors.descriptionIsRequiredGr")];
                break;
            case 2:
                if (!listingData.region_id) newErrors.region_id = [t("admin.listingsForms.stepErrors.regionIsRequired")];
                if (!listingData.county_id) newErrors.county_id = [t("admin.listingsForms.stepErrors.countyIsRequired")];
                if (!listingData.municipality_id) newErrors.municipality_id = [t("admin.listingsForms.stepErrors.municipalityIsRequired")];
                if (!listingData.municipality_id) newErrors.municipality_id = [t("admin.listingsForms.stepErrors.municipalityIsRequired")];
                if (!listingData.address_number) newErrors.address_number = [t("admin.listingsForms.stepErrors.addressNumberIsRequired")];
                if (!listingData.address_street || !listingData.address_street_gr) newErrors.address_street = [t("admin.listingsForms.stepErrors.addressStreetIsRequired")];
                if (!listingData.address_street_gr || !listingData.address_street) newErrors.address_street_gr = [t("admin.listingsForms.stepErrors.addressStreetIsRequiredGr")];
                if (!listingData.postcode) newErrors.postcode = [t("admin.listingsForms.stepErrors.postcodeIsRequired")];
                if (!listingData.latitude || listingData.latitude === "0.0") newErrors.latitude = [t("admin.listingsForms.stepErrors.latitudeIsRequired")];
                if (!listingData.longitude || listingData.longitude === "0.0") newErrors.longitude = [t("admin.listingsForms.stepErrors.longitudeIsRequired")];
                break;
            case 3:
                // Residential/Commercial common fields
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.floor_area) {
                    newErrors.floor_area = [t("admin.listingsForms.stepErrors.floorAreaIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.wc) {
                    newErrors.wc = [t("admin.listingsForms.stepErrors.wcIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.floor) {
                    newErrors.floor = [t("admin.listingsForms.stepErrors.floorIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.bathrooms) {
                    newErrors.bathrooms = [t("admin.listingsForms.stepErrors.bathroomsIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.levels) {
                    newErrors.levels = [t("admin.listingsForms.stepErrors.levelsIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.heating_system) {
                    newErrors.heating_system = [t("admin.listingsForms.stepErrors.heatingSystemIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.energy_class) {
                    newErrors.energy_class = [t("admin.listingsForms.stepErrors.energyClassIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.power_type) {
                    newErrors.power_type = [t("admin.listingsForms.stepErrors.powerTypeIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.floor_type) {
                    newErrors.floor_type = [t("admin.listingsForms.stepErrors.floorTypeIsRequired")];
                }
                if ((listingData.type === 'residential' || listingData.type === 'commercial') && !listingData.construction_year) {
                    newErrors.construction_year = [t("admin.listingsForms.stepErrors.constructionYearIsRequired")];
                }
                // Residential specific fields
                if (listingData.type === 'residential' && !listingData.bedrooms) {
                    newErrors.bedrooms = [t("admin.listingsForms.stepErrors.bedroomsIsRequired")];
                }
                if (listingData.type === 'residential' && !listingData.kitchens) {
                    newErrors.kitchens = [t("admin.listingsForms.stepErrors.kitchensIsRequired")];
                }
                if (listingData.type === 'residential' && !listingData.living_rooms) {
                    newErrors.living_rooms = [t("admin.listingsForms.stepErrors.livingRoomsIsRequired")];
                }
                //  Commercial specific fields
                if (listingData.type === 'commercial' && !listingData.rooms) {
                    newErrors.rooms = [t("admin.listingsForms.stepErrors.roomsIsRequired")];
                }
                // Land
                if (listingData.type === 'land' && !listingData.land_area) {
                    newErrors.land_area = [t("admin.listingsForms.stepErrors.landAreaIsRequired")];
                }
                if (listingData.type === 'land' && !listingData.zone) {
                    newErrors.zone = [t("admin.listingsForms.stepErrors.zoneIsRequired")];
                }
                if (listingData.type === 'land' && !listingData.slope) {
                    newErrors.slope = [t("admin.listingsForms.stepErrors.slopeIsRequired")];
                }
                if (listingData.type === 'land' && !listingData.orientation) {
                    newErrors.orientation = [t("admin.listingsForms.stepErrors.orientationIsRequired")];
                }
                if (listingData.type === 'land' && !listingData.view) {
                    newErrors.view = [t("admin.listingsForms.stepErrors.viewIsRequired")];
                }
                if (listingData.type === 'land' && !listingData.length_of_facade) {
                    newErrors.length_of_facade = [t("admin.listingsForms.stepErrors.lengthOfFacadeIsRequired")];
                }
                break;
            case 4:
                break;
            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            if (!completedSteps.includes(currentStep)) {
                setCompletedSteps([...completedSteps, currentStep]);
            }
            setCurrentStep(currentStep + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            antMessage.error('Please fill in all required fields');
        }
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToStep = (step) => {
        if (completedSteps.includes(step) || step === Math.max(...completedSteps, -1) + 1) {
            setCurrentStep(step);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const renderTextField = (fieldName, label, type = "text", rows = 1) => (
        <Form.Group controlId={fieldName}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
                className={styles.Input}
                type={type}
                name={fieldName}
                value={listingData[fieldName]}
                onChange={handleChange}
                as={type === "textarea" ? "textarea" : undefined}
                rows={rows}
            />
            {errors?.[fieldName]?.map((message, idx) => (
                <Alert className={styles.Input} variant="warning" key={idx}>
                    {message}
                </Alert>
            ))}
        </Form.Group>
    );

    const buildFormData = (isDraft) => {
        const formData = new FormData();
        formData.append("type", listingData.type);
        formData.append("sub_type", listingData.sub_type || "");
        formData.append("sale_type", listingData.sale_type);
        formData.append("price", listingData.price || "0");
        formData.append("currency", listingData.currency);
        formData.append("description", listingData.description || "");
        formData.append("description_gr", listingData.description_gr || "");
        formData.append("address_number", listingData.address_number || "0");
        formData.append("address_street", listingData.address_street || "");
        formData.append("address_street_gr", listingData.address_street_gr || "");
        formData.append("postcode", listingData.postcode || "");
        formData.append("floor_area", listingData.floor_area || "0");
        formData.append("land_area", listingData.land_area || "0");
        formData.append("levels", listingData.levels || "0");
        formData.append("bedrooms", listingData.bedrooms || "0");
        formData.append("wc", listingData.wc || "0");
        formData.append("floor", listingData.floor || "0");
        formData.append("kitchens", listingData.kitchens || "0");
        formData.append("bathrooms", listingData.bathrooms || "0");
        formData.append("living_rooms", listingData.living_rooms || "0");
        formData.append("heating_system", listingData.heating_system || "");
        formData.append("energy_class", listingData.energy_class || "");
        formData.append("power_type", listingData.power_type || "");
        formData.append("floor_type", listingData.floor_type || "");
        formData.append("construction_year", listingData.construction_year || "");
        formData.append("latitude", listingData.latitude);
        formData.append("longitude", listingData.longitude);
        formData.append("cover_coefficient", listingData.cover_coefficient || "0");
        formData.append("building_coefficient", listingData.building_coefficient || "0");
        formData.append("length_of_facade", listingData.length_of_facade || "0");
        formData.append("orientation", listingData.orientation || "");
        formData.append("view", listingData.view || "");
        formData.append("slope", listingData.slope || "");
        formData.append("zone", listingData.zone || "");
        formData.append("distance_from_sea", listingData.distance_from_sea || "0");
        formData.append("rooms", listingData.rooms || "0");
        formData.append("approved", listingData.approved);
        formData.append("featured", listingData.featured);
        formData.append("is_draft", isDraft);
        formData.append("is_first", "0");
        formData.append("region_id", listingData.region_id);
        formData.append("county_id", listingData.county_id);
        formData.append("municipality_id", listingData.municipality_id);
        formData.append("listing_owner", listingData.listing_owner);
        formData.append("video_url", listingData.video_url);

        selectedAmenities.forEach((amenity) => {
            formData.append("amenities_ids", parseInt(amenity, 10));
        });

        if (uploadedImages.length > 0) {
            uploadedImages.forEach((image) => {
                formData.append('uploaded_images', image.file);
                formData.append('image_orders', image.order);
                formData.append('image_descriptions', image.description || "");
            });
        }

        return formData;
    };

    const handleSaveDraft = async () => {
        setIsSavingDraft(true);
        try {
            const formData = buildFormData(true);
            if (draftId) {
                await axiosReq.put(`/listings/${draftId}/`, formData);
            } else {
                const { data } = await axiosReq.post("/listings/", formData);
                setDraftId(data.id);
            }
            antMessage.success('Draft saved successfully!');
        } catch (err) {
            antMessage.error('Failed to save draft.');
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const formData = buildFormData(false);

        try {
            let data;
            if (draftId) {
                ({ data } = await axiosReq.put(`/listings/${draftId}/`, formData));
            } else {
                ({ data } = await axiosReq.post("/listings/", formData));
            }
            antMessage.success('Listing created successfully!');
            history.push(`/listings/${data.id}`);
        } catch (err) {
            setErrors(err.response?.data || {});
            if (err.code === 'ECONNABORTED' || err.timeout || err.code === 'ERR_NETWORK') {
                // Request timed out - listing might be created
                antMessage.warning({
                    content: (
                        <div>
                            <p><strong>Upload is taking longer than expected.</strong></p>
                            <p>The listing may have been created. Please check the listings page.</p>
                            <p>Redirecting in 3 seconds...</p>
                        </div>
                    ),
                    duration: 15,
                });

                // Redirect to listings page after 3 seconds
                setTimeout(() => {
                    history.push('/frontend/admin/listings');
                }, 3000);
            } else if (err.response?.status === 503) {
                antMessage.error({
                    content: 'Server is temporarily unavailable. Please try again in a moment.',
                    duration: 10,
                });
                setIsSubmitting(false);
            } else {
                // Regular form errors
                setErrors(err.response?.data || {});
                antMessage.error('Failed to create listing. Please check the form.');
                setIsSubmitting(false);
            }
        }
    };

    const steps = [
        { title: t("admin.listingsForms.images"), icon: <PictureOutlined /> },
        { title: t("admin.listingsForms.basicInfo"), icon: <InfoCircleOutlined /> },
        { title: t("admin.listingsForms.location"), icon: <EnvironmentOutlined /> },
        { title: t("admin.listingsForms.details"), icon: <ToolOutlined /> },
        { title: t("admin.listingsForms.amenities"), icon: <SaveOutlined /> },
        { title: t("admin.listingsForms.review"), icon: <CheckCircleOutlined /> },
    ];

    if (!userStatus?.canAccessListings) {
        return <Forbidden403 />;
    }

    if (!ownersLoaded) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '94px 24px 24px 24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Card style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <Title level={2} style={{ textAlign: 'center', marginBottom: '32px' }}>
                    {t("admin.listingsForms.addListing")}
                </Title>

                <Steps
                    current={currentStep}
                    items={steps.map((item, index) => {
                        const isCompleted = completedSteps.includes(index);
                        const isAccessible = isCompleted || index === Math.max(...completedSteps, -1) + 1;

                        let stepIcon;
                        if (isCompleted) {
                            stepIcon = <CheckCircleOutlined />;
                        } else if (isAccessible) {
                            stepIcon = item.icon || <InfoCircleOutlined />;
                        } else {
                            stepIcon = <LockOutlined />;
                        }

                        return {
                            title: item.title,
                            icon: stepIcon,
                            disabled: !isAccessible,
                            style: {
                                cursor: isAccessible ? 'pointer' : 'not-allowed',
                                opacity: isAccessible ? 1 : 0.5
                            }
                        };
                    })}
                    onChange={(current) => {
                        // Handle click
                        const isAccessible = completedSteps.includes(current) ||
                            current === Math.max(...completedSteps, -1) + 1;
                        if (isAccessible) {
                            goToStep(current);
                        }
                    }}
                    style={{ marginBottom: '32px' }}
                />

                <div style={{ minHeight: '400px' }}>
                    {/* Step 0: Images */}
                    {currentStep === 0 && (
                        <div>
                            <Title level={4}>{t("admin.listingsForms.uploadImages")}</Title>
                            <Card variant="borderless" style={{ marginBottom: 24 }}>
                                <AntDForm.Item
                                    label={"Video URL"}
                                    help={errors?.video_url?.[0]}
                                    validateStatus={errors?.video_url ? "error" : ""}
                                >
                                    <Input
                                        name="video_url"
                                        value={listingData.video_url || ""}
                                        onChange={handleChange}
                                        placeholder={"Video URL"}
                                        size="large"
                                    />
                                </AntDForm.Item>
                            </Card>
                            <DragDropImageUpload
                                uploadedImages={uploadedImages}
                                setUploadedImages={setUploadedImages}
                                error={errors?.images}
                                maxImages={40}
                                setExistingImages={setExistingImages}
                                existingImages={existingImages}
                                imagesToDelete={imagesToDelete}
                                setImagesToDelete={setImagesToDelete}
                            />
                        </div>
                    )}

                    {/* Step 1: Basic Info */}
                    {currentStep === 1 && (
                        <div>
                            <Title level={4}>{t("admin.listingsForms.basicInfo")}</Title>
                            <Owner
                                listingData={listingData}
                                handleChange={handleChange}
                                handleShow={handleShow}
                                handleClose={handleClose}
                                show={show}
                                owners={owners}
                                errors={errors}
                            />
                            <TypeSaleSub
                                listingData={listingData}
                                handleChange={handleChange}
                                owners={owners}
                                errors={errors}
                                t={t}
                            />
                            <CurrencyPriceAvailability
                                listingData={listingData}
                                handleChange={handleChange}
                                errors={errors}
                                t={t}
                            />
                            <Description
                                listingData={listingData}
                                handleChange={handleChange}
                                errors={errors}
                                t={t}
                            />
                        </div>
                    )}

                    {/* Step 2: Location */}
                    {currentStep === 2 && (
                        <div>
                            <Title level={4}>{t("admin.listingsForms.locationDetails")}</Title>
                            <LocationFields
                                listingData={listingData}
                                handleChange={handleChange}
                                errors={errors}
                                onRegionChange={handleRegionChange}
                                onCountyChange={handleCountyChange}
                                onMunicipalityChange={handleMunicipalityChange}
                                selectedRegion={selectedRegion}
                                selectedCounty={selectedCounty}
                                selectedMunicipality={selectedMunicipality}
                                renderTextField={renderTextField}
                                t={t}
                            />
                        </div>
                    )}

                    {/* Step 3: Technical Details */}
                    {currentStep === 3 && (
                        <div>
                            <Title level={4}>{t("admin.listingsForms.technicalDetails")}</Title>
                            {listingData.type === 'residential' && (
                                <ResidentialFields
                                    listingData={listingData}
                                    handleChange={handleChange}
                                    errors={errors}
                                    t={t}
                                    renderTextField={renderTextField}
                                    handleAmenityChange={handleAmenityChange}
                                    selectedAmenities={selectedAmenities}
                                />
                            )}
                            {listingData.type === 'commercial' && (
                                <CommercialFields
                                    listingData={listingData}
                                    handleChange={handleChange}
                                    errors={errors}
                                    t={t}
                                    renderTextField={renderTextField}
                                    handleAmenityChange={handleAmenityChange}
                                    selectedAmenities={selectedAmenities}
                                />
                            )}
                            {listingData.type === 'land' && (
                                <LandFields
                                    listingData={listingData}
                                    handleChange={handleChange}
                                    errors={errors}
                                    t={t}
                                />
                            )}
                        </div>
                    )}
                    {/* Step 4: Amenities */}
                    {currentStep === 4 && (
                        <div>
                            <Title level={4}>{t("admin.listingsForms.technicalDetails")}</Title>
                            {listingData.type === 'residential' && (
                                <AmenitiesResidential
                                    handleAmenityChange={handleAmenityChange}
                                    selectedAmenities={selectedAmenities}
                                    create={true}
                                />
                            )}
                            {listingData.type === 'commercial' && (
                                <AmenitiesCommercial
                                    handleAmenityChange={handleAmenityChange}
                                    selectedAmenities={selectedAmenities}
                                    create={true}
                                />
                            )}
                            {listingData.type === 'land' && (
                                <AmenitiesLand
                                    handleAmenityChange={handleAmenityChange}
                                    selectedAmenities={selectedAmenities}
                                    create={true}
                                />
                            )}
                            <Card
                                variant={false}
                                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '24px' }}
                            >
                                {userStatus?.canApprove && (
                                    <ApprovedFeatureCheckbox
                                        listingData={listingData}
                                        handleChecked={handleChecked}
                                        errors={errors}
                                        t={t}
                                    />
                                )}
                            </Card>
                        </div>
                    )}

                    {/* Step 5: Review */}
                    {currentStep === 5 && (
                        <div>
                            <Title level={4} style={{ textAlign: 'center' }}>{t("admin.listingsForms.reviewYourListing")}</Title>
                            <Descriptions variant="bordered" column={2} style={{ marginTop: '24px' }}>
                                <Descriptions.Item label={t("admin.listingsForms.images")} span={2}>
                                    <Tag color="blue">{uploadedImages.length} {t("admin.listingsForms.images")}</Tag>
                                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goToStep(0)}>{t("admin.listingsForms.edit")}</Button>
                                </Descriptions.Item>
                                <Descriptions.Item label={t("propertyDetails.type")}>{listingData.type || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t("propertyDetails.typeSale")}>{listingData.sale_type || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t("propertyDetails.price")}>
                                    {listingData.currency} {listingData.price || '-'}
                                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goToStep(1)}>{t("admin.listingsForms.edit")}</Button>
                                </Descriptions.Item>
                                <Descriptions.Item label={t("propertyDetails.owner")}>{owner[listingData.listing_owner] || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t("propertyDetails.location")} span={2}>
                                    {t("regionOptions.region")}: {regionName}, {t("regionOptions.county")}: {countyName}, {t("regionOptions.municipality")}: {municipalityName || '-'}
                                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goToStep(2)}>{t("admin.listingsForms.edit")}</Button>
                                </Descriptions.Item>
                                <Descriptions.Item label={t("amenities.title")} span={2}>
                                    <Tag color="purple">{selectedAmenities.length} selected</Tag>
                                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goToStep(3)}>{t("admin.listingsForms.edit")}</Button>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                    <Space>
                        {currentStep > 0 && <Button size="large" onClick={prevStep}>{t("admin.listingsForms.previous")}</Button>}
                        <Button size="large" icon={<CloseOutlined />} onClick={() => history.push('/frontend/admin/listings')}>{t("admin.listingsForms.cancel")}</Button>
                    </Space>

                    <Space>
                        {currentStep < 5 && (
                            <Button
                                size="large"
                                icon={<SaveOutlined />}
                                loading={isSavingDraft}
                                onClick={handleSaveDraft}
                            >
                                {draftId ? t("admin.listingsForms.updateDraft") : t("admin.listingsForms.saveDraft")}
                            </Button>
                        )}
                        {currentStep < 5 ? (
                            <Button
                                type="primary"
                                size="large"
                                onClick={nextStep}
                                style={{ backgroundColor: '#847c3d', borderColor: '#847c3d' }}
                            >
                                {t("admin.listingsForms.continue")}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                size="large"
                                icon={<SaveOutlined />}
                                loading={isSubmitting}
                                onClick={handleSubmit}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                {t("admin.listingsForms.submitListing")}
                            </Button>
                        )}
                    </Space>
                </div>
            </Card>
        </div>
    );
}

export default AdminListingCreateForm;